/**
 * Post-auth routing for activation (CEO: time-to-value).
 * Matches docs/ENTERWEU-USERFLOWS.md § Flow A activation order.
 */
import {
  getHomePathForUser,
  getUserType,
  USER_TYPES,
} from './userTypeUtils';
import { getCandidateJourney, getConsultancyJourney } from './journeyProgress';

/**
 * Prefer deep-link `from` when safe; otherwise first incomplete journey step, else home.
 */
export const getPostLoginPath = (user, fromPath) => {
  if (fromPath && fromPath !== '/login' && fromPath !== '/register' && !fromPath.startsWith('/register')) {
    return fromPath;
  }

  const userKey = user?.id || user?.username;
  const type = getUserType(user);

  if (type === USER_TYPES.CANDIDATE) {
    const j = getCandidateJourney(userKey);
    // 1) Choose practice format
    if (!j.modeSelected && !j.domainSelected) return '/interview-modes';
    // 2) JD plan — do not skip just because a mode was opened
    if (!j.jdPlanCreated) return '/job-descriptions';
    // 3) First scored mock / mode practice
    if (!j.firstMockCompleted) return '/mock-interviews';
    // 4) Discoverability hint (optional opt-in; flag set when profile tip is seen/skipped)
    if (!j.discoverableHintSeen) return '/profile';
  }

  if (type === USER_TYPES.CONSULTANCY) {
    const j = getConsultancyJourney(userKey);
    if (!j.companyAdded) return '/client-companies';
    if (!j.requirementAdded) return '/client-companies';
    // Invite or search talent before billing pressure
    if (!j.talentSearched && !j.candidateInvited) return '/talent-discovery';
    if (!j.packSubscribed && !j.firstUnlock) return '/subscription-billing';
  }

  if (type === USER_TYPES.PARTNER) {
    return '/partner/dashboard';
  }

  return getHomePathForUser(user);
};

export const getSubscribeNextStep = (user, plan = {}) => {
  const kind = (plan.plan_kind || plan.kind || '').toLowerCase();
  const type = getUserType(user);

  if (type === USER_TYPES.INSTITUTION) {
    return {
      title: 'Institution plan ready',
      message: 'Open your enrolled students, then schedule interview rounds from the institution workspace.',
      href: '/institution/learners',
      cta: 'Open students',
    };
  }

  if (type === USER_TYPES.CONSULTANCY || kind.includes('talent')) {
    if (kind.includes('talent') || kind === 'talent_pack') {
      return {
        title: 'Talent pack active',
        message: 'Search opted-in candidates and unlock contacts when you are ready to shortlist.',
        href: '/talent-discovery',
        cta: 'Open Talent Discovery',
      };
    }
    return {
      title: 'Interview pack active',
      message: 'Invite candidates, schedule interviews, and track your CRM pipeline.',
      href: '/candidate-management',
      cta: 'Manage candidates',
    };
  }

  if (type === USER_TYPES.INSTITUTION || kind.includes('institution')) {
    return {
      title: 'Institution plan ready',
      message: 'Add learners, then schedule interview rounds from the institution workspace.',
      href: '/institution/learners',
      cta: 'Open learners',
    };
  }

  return {
    title: 'Plan activated',
    message: 'Run a mock interview to earn your readiness score.',
    href: '/mock-interviews',
    cta: 'Start a mock interview',
  };
};
